import React, { useState, useRef, useMemo } from 'react';
import {
  CircularProgressContainer,
  CircularProgressSVG,
  CircularProgressBackground,
  CircularProgressFill,
  CircularProgressInner,
  CircularProgressText,
  Tooltip,
  TooltipLabel,
  TooltipValue
} from './SelfEvaluationStyles';
import { useTranslation } from 'react-i18next';

// Circular Progress Chart Component with dynamic concentric rings
const CircularProgress = ({ kpiScores }) => {
  const [hoveredRing, setHoveredRing] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  const { t } = useTranslation();

  // Get the keys from kpiScores excluding total_score
  const kpiKeys = useMemo(() => {
    if (!kpiScores) return [];
    return Object.keys(kpiScores).filter(key => key !== 'total_score');
  }, [kpiScores]);

  // Calculate dynamic stroke widths based on number of rings
  const strokeWidths = useMemo(() => {
    const baseWidth = 18;
    const numRings = kpiKeys.length;
    // Adjust width based on number of rings
    const adjustedWidth = numRings > 4 ? baseWidth * 0.83 : baseWidth;
    return Array(numRings).fill(adjustedWidth);
  }, [kpiKeys.length]);

  // Calculate radii based on stroke width to ensure no gaps
  const radii = useMemo(() => {
    const outerRadius = 105;
    return strokeWidths.reduce((acc, width, index) => {
      const previousRadii = acc.length > 0 ? acc[acc.length - 1] : outerRadius;
      acc.push(previousRadii - width);
      return acc;
    }, []);
  }, [strokeWidths]);

  // Define the standard colors for the rings
  // #90A4AE
  const ringColors = ["#7986CB", "#4DB6AC", "#9CCC65", "#FF7043", "#FFB74D"];

  // Define background colors for alternating rings
  const backgroundColors = ["#E6E6E6", "#F3F3F3", "#E5E7EB", "#F3F3F3", "#E5E7EB"];

  // Map kpi keys to labels
  const ringLabels = useMemo(() => {
    return kpiKeys.map(key => {
      return key;
    });
  }, [kpiKeys]);

  // Function to determine which ring is being hovered based on distance from center
  const determineHoveredRing = (x, y) => {
    if (!containerRef.current) return null;

    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    // Calculate distance from center
    const distanceFromCenter = Math.sqrt(
      Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2)
    );

    // Convert distance to SVG coordinate space
    const svgDistance = (distanceFromCenter / rect.width) * 200;

    // Determine which ring based on distance
    for (let i = 0; i < radii.length; i++) {
      if (svgDistance <= radii[i] + strokeWidths[i] / 2 &&
          svgDistance >= radii[i] - strokeWidths[i] / 2) {
        return i;
      }
    }

    return null;
  };

  // Handle mouse move to update tooltip position and determine hovered ring
  const handleMouseMove = (event) => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const hoveredIndex = determineHoveredRing(x, y);
    setHoveredRing(hoveredIndex);

    if (hoveredIndex !== null) {
      setTooltipPosition({ x, y });
    }
  };

  // Handle mouse leave
  const handleMouseLeave = () => {
    setHoveredRing(null);
  };

  // Get percentage value from kpiScores
  const getPercentage = (key) => {
    const score = kpiScores[key];
    if (!score) return 0;

    if (key === 'mashaer_presence') {
      return score.details?.days_present * 20 || 0;
    }
    return score.details?.percentage || 0;
  };

  return (
    <CircularProgressContainer
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <CircularProgressSVG viewBox="0 0 200 200">
        {/* Create dynamic concentric rings based on kpiScores */}
        {kpiKeys.map((key, index) => {
          const radius = radii[index];
          const strokeWidth = strokeWidths[index];
          const circumference = 2 * Math.PI * radius;
          const color = ringColors[index];
          const bgColor = backgroundColors[index];
          const percentage = getPercentage(key);

          return (
            <g key={key}>
              <CircularProgressBackground
                cx="100"
                cy="100"
                r={radius}
                strokeWidth={strokeWidth}
                style={{ stroke: bgColor }}
              />
              <CircularProgressFill
                cx="100"
                cy="100"
                r={radius}
                percentage={percentage}
                circumference={circumference}
                color={color}
                strokeWidth={strokeWidth}
              />
            </g>
          );
        })}
      </CircularProgressSVG>
      <CircularProgressInner>
        <CircularProgressText>{kpiScores?.total_score?.toFixed(1) || 0}%</CircularProgressText>
      </CircularProgressInner>

      {/* Tooltip */}
      {hoveredRing !== null && (
        <Tooltip
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y - 40}px`
          }}
        >
          <TooltipLabel>{t(ringLabels[hoveredRing])}</TooltipLabel>
          <TooltipValue>{getPercentage(kpiKeys[hoveredRing]).toFixed(2)}%</TooltipValue>
        </Tooltip>
      )}
    </CircularProgressContainer>
  );
};

export default CircularProgress;
